import React, { useState } from 'react';
import '../pages/Pages.css';
import api from '../../services/api';
import AdSense from '../AdSense';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (loading) return; // Prevent multiple submissions
    
    setLoading(true);
    setError('');

    try {
      const response = await api.post('/contact', formData);
      
      if (response.data && response.data.success) {
        setSubmitted(true);
        setFormData({ name: '', email: '', subject: '', message: '' });
        setTimeout(() => {
          setSubmitted(false);
        }, 5000);
      } else {
        setError(response.data?.message || 'Failed to send message. Please try again.');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.errors?.message?.[0] ||
                          'Failed to send message. Please try again or email us directly at info@kenyanfinancialhub.site';
      setError(errorMessage);
      console.error('Error sending message:', err);
    } finally {
      setLoading(false);
    }
  };

  // Contact details - hardcoded to ensure they display correctly
  const email = 'info@kenyanfinancialhub.site';
  const phone = '+254 748 344 096';
  const phoneNumber = '+254748344096';
  const whatsappLink = `https://wa.me/${phoneNumber}`;

  return (
    <div className="page-container" key="contact-page">
      <div className="page-header">
        <h1 className="page-title">
          <span className="title-icon">📧</span>
          Contact Us
        </h1>
        <p className="page-subtitle">We'd love to hear from you</p>
      </div>

      <div className="page-content">
        {/* Top Banner Ad - Only shows if valid ad slot is configured */}
        {process.env.REACT_APP_ADSENSE_SLOT_TOP && (
          <AdSense 
            adSlot={process.env.REACT_APP_ADSENSE_SLOT_TOP}
            adFormat="auto"
            fullWidthResponsive={true}
            className="adsense-banner"
          />
        )}
        
        <div className="contact-intro">
          <p>
            Have a question, suggestion, or feedback? We're here to help! Fill out the form below 
            or reach out to us through any of the contact methods listed.
          </p>
        </div>

        <div className="contact-grid">
          <div className="contact-info">
            <h2>Get in Touch</h2>
            <div className="contact-item">
              <div className="contact-icon">📧</div>
              <div>
                <h3>Email</h3>
                <a href={`mailto:${email}`} className="contact-link" onClick={(e) => e.stopPropagation()}>
                  {email}
                </a>
              </div>
            </div>
            <div className="contact-item">
              <div className="contact-icon">📱</div>
              <div>
                <h3>Phone</h3>
                <a 
                  href={whatsappLink} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="contact-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  {phone}
                </a>
              </div>
            </div>
            <div className="contact-item">
              <div className="contact-icon">📍</div>
              <div>
                <h3>Address</h3>
                <p>Nairobi, Kenya</p>
                <p>East Africa</p>
              </div>
            </div>
            <div className="contact-map">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3988.7975!2d36.8219!3d-1.2921!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x182f1172d84d49a7%3A0xf7cf0254b297924c!2sNairobi!5e0!3m2!1sen!2ske!4v1698765432100!5m2!1sen!2ske"
                width="100%"
                height="250"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Nairobi, Kenya Location"
              ></iframe>
            </div>
          </div>

          <div className="contact-form-wrapper">
            <form className="contact-form" onSubmit={handleSubmit} noValidate>
              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Your full name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="your.email@example.com"
                />
              </div>
              <div className="form-group">
                <label htmlFor="subject">Subject *</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  placeholder="What is this regarding?"
                />
              </div>
              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="6"
                  placeholder="Tell us more about your inquiry..."
                ></textarea>
              </div>
              <button type="submit" className="submit-button" disabled={loading}>
                {loading ? 'Sending...' : submitted ? '✓ Message Sent!' : 'Send Message'}
              </button>
              {error && (
                <p className="error-message">
                  {error}
                </p>
              )}
              {submitted && (
                <p className="success-message">
                  Thank you for contacting us! We'll get back to you soon.
                </p>
              )}
            </form>
          </div>
        </div>
        
        {/* Bottom Banner Ad - Only shows if valid ad slot is configured */}
        {process.env.REACT_APP_ADSENSE_SLOT_BOTTOM && (
          <AdSense 
            adSlot={process.env.REACT_APP_ADSENSE_SLOT_BOTTOM}
            adFormat="auto"
            fullWidthResponsive={true}
            className="adsense-banner"
          />
        )}
      </div>
    </div>
  );
};

export default ContactPage;
